import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import InteraktScript from "../../common/InteraktScript";
import "./../home/home.css";

function Cherishcare() {
  const [userid, setUserid] = useState(0);
  const [activeCareSlide, setActiveCareSlide] = useState(0);
  const supportViewportRef = useRef(null);
  const ecosystemRowRefs = useRef([]);
  const ecosystemAutoIndexRef = useRef([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const userIdValue = localStorage.getItem("userId");
    setUserid(userIdValue);
  }, []);

  const containerRef = useRef(null);
  const rowRef = useRef(null); 
  const autoIndexRef = useRef(0);
  const [activeStep, setActiveStep] = useState(0);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "center center"], 
  });

  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });

  useEffect(() => {
    const node = rowRef.current;
    let isPaused = false;

    if (!node) {
      return undefined;
    }

    const autoSlide = () => {
      const cards = Array.from(node.querySelectorAll(".journey-card-wrapper"));

      if (isPaused || window.innerWidth >= 992 || cards.length === 0) {
        return;
      }

      autoIndexRef.current = (autoIndexRef.current + 1) % cards.length;
      node.scrollTo({
        left: cards[autoIndexRef.current].offsetLeft,
        behavior: "smooth",
      });
    };

    const pauseSlider = () => {
      isPaused = true;
    };

    const resumeSlider = () => {
      isPaused = false;
      autoIndexRef.current = activeStep;
    };

    node.addEventListener("touchstart", pauseSlider, { passive: true });
    node.addEventListener("touchend", resumeSlider);
    node.addEventListener("touchcancel", resumeSlider);
    node.addEventListener("pointerdown", pauseSlider);
    node.addEventListener("pointerup", resumeSlider);
    node.addEventListener("pointercancel", resumeSlider);

    const intervalId = window.setInterval(autoSlide, 2000);

    return () => {
      window.clearInterval(intervalId);
      node.removeEventListener("touchstart", pauseSlider);
      node.removeEventListener("touchend", resumeSlider);
      node.removeEventListener("touchcancel", resumeSlider);
      node.removeEventListener("pointerdown", pauseSlider);
      node.removeEventListener("pointerup", resumeSlider);
      node.removeEventListener("pointercancel", resumeSlider);
    };
  }, [activeStep]);

  const handleScroll = () => {
    if (rowRef.current) {
      const cards = Array.from(rowRef.current.querySelectorAll(".journey-card-wrapper"));
      const scrollLeft = rowRef.current.scrollLeft;
      let index = 0;

      cards.forEach((card, cardIndex) => {
        if (Math.abs(card.offsetLeft - scrollLeft) < Math.abs((cards[index]?.offsetLeft ?? 0) - scrollLeft)) {
          index = cardIndex;
        }
      });

      setActiveStep(index);
    }
  };

  const steps = [
    {
      id: 1,
      title: "A Circle of 9 Elite Experts",
      desc: <>You are backed by a synchronized, integrated team of Tier-1 obstetricians,
              general physicians, pediatricians, nutritionists, lactation experts, yoga experts, and
              emotional health coaches. They collaborate and sync on one single platform, ensuring
              they all are on the same page and you never have to repeat your medical history.</>,
      img: "/overwhelming1.png", 
      trigger: 0.1, 
    },
    {
      id: 2,
      title: "AI-Powered Daily Health Tracking",
      desc: <>We don’t wait for your next monthly scan. Our intelligent AI-powered health
            tracker tracks your vitals (sugar, BP), symptoms, mood, and sleep pattern daily,
            detecting risks like GDM, hypertension, or HELLP before they become complications to
            ensure maximum safety for you and your baby.</>,
      img: "/overwhelming2.png",
      trigger: 0.5, 
    },
    {
      id: 3,
      title: "Ultimate Convenience",
      desc: <>Manage your daily care right from your phone, while essential physical
            checkups and ultrasounds take place at our premium Myprega clinics—guaranteed to be
            within a 10-minute drive. Say goodbye to long waiting hours at clinics, rushed consults,
            half-day leaves for scans, and endless calls to schedule—we seamlessly adjust our
            schedule to fit your busy life to give you the ultimate convenience.</>,
      img: "/overwhelming3.35445.png",
      trigger: 0.9, 
    },
    {
      id: 4,
      title: "True Holistic Wellness",
      desc: <>Medical safety meets emotional peace. Experience daily Garbhasanskar, live
           prenatal yoga, and 1-on-1 lactation and emotional health coaching to prepare you for
           motherhood.</>,
      img: "/overwhelming4.png",
      trigger: 0.9, 
    },
    {
      id: 5,
      title: "100% Transparent, All-Inclusive Care",
      desc: <>Your entire journey is managed end-to-end on our innovative tech platform.
            Enjoy the ultimate convenience of having every medical and wellness need seamlessly
            handled under one roof at your fingertips, with absolutely zero hidden costs.</>,
      img: "/overwhelming5.png",
      trigger: 0.9, 
    },
  ];


  const cherishSupportCards = [
    {
      title: "Expert Care is a Daily Standard, Not a Luxury",
      desc: "In the traditional system, speaking to your doctor is a rare monthly event. At Myprega, daily engagement with your medical team—who actively monitor your progress daily and provide tailored advice to you 24/7—is our baseline standard. And for those unlimited doubts, meet NurtureAI: your highly advanced, 24/7 intelligent companion. Specifically trained to handle complex pregnancy queries, it instantly analyzes your unique health profile to deliver personalized, deeply reassuring solutions whenever you need them most",
      img: "/LRcard6.png",
    },
    {
      title: "The End of Messy Medical Files",
      desc: "Forget carrying heavy folders to every appointment. Your 44 consultations, 9 ultrasound scans, and 40+ at-home lab tests are flawlessly organized in one end-to-end encrypted dashboard. It’s total privacy, absolute security, and lifetime access at your fingertips.",
      img: "/LRcard2.png",
    },
  ];


  const ecosystemSections = [
    {
      group: "1. Elite Clinical Care & Diagnostics",
      cards: [
        {
          icon: "🩺",
          title: "The 9-Expert Medical Board",
          desc: "Gynecology, fetal medicine, genetics, internal medicine, nutritionists, emotional health coaches, pediatrics, yoga experts, and more collaborating on your case daily. It’s a synchronized brain trust providing a level of safety no single doctor or clinic can offer.",
        },
        {
          icon: "🏥",
          title: "44+ Unhurried Consultations",
          desc: "A perfectly structured mix of online and in-clinic visits across all specialties. No rushed 5-minute appointments; just deeply personalized, trimester-specific care.",
        },
        {
          icon: "🩸",
          title: "40+ Advanced Lab & Genetic Tests",
          desc: "From complete blood panels to thyroid and genetic screenings, all pre-scheduled and managed by us. Results flow directly to your doctors, keeping your care proactively one step ahead.",
        },
        {
          icon: "🖼",
          title: "9 Strategically Timed Ultrasounds",
          desc: "Including Fetal Echo, Growth Scans, and Dopplers, all reviewed by fetal medicine experts to ensure you never miss a single developmental milestone.",
        },
      ],
    },
    {
      group: "2. The Proactive Tech Sanctuary",
      cards: [
        {
          icon: "💬",
          title: "24/7 Direct Gynecologist WhatsApp Access",
          desc: "Skip the midnight internet rabbit hole. Text your exact symptoms or doubts directly to your gynecologist for instant, reliable medical answers.",
        },
        {
          icon: "🛡",
          title: "AI-Powered Daily Health Tracking",
          desc: "Log your BP, weight, symptoms, and fetal movements from your phone. Your medical team monitors this daily, proactively flagging any complication long before it becomes a risk.",
        },
        {
          icon: "🚨",
          title: "Instant 24/7 Emergency Support",
          desc: "Whether it’s late-night cramping or unexpected spotting, our emergency response doctors are just one tap away, any day, any time.",
        },
        {
          icon: "📱",
          title: 'The "Command Center" Dashboard',
          desc: "Manage your doctor visits, test reports, prescriptions, and live sessions in one beautifully organized, end-to-end encrypted digital space. You are finally in total control.",
        },
      ],
    },
    {
      group: "3. Holistic Wellness & Mind-Body Alignment",
      cards: [
        {
          icon: "🥗",
          title: "Personalized Monthly Nutrition",
          desc: "Month-specific diet plans with weekly follow-ups from your dedicated coach. Safely manage weight, optimize baby growth, and sustain your energy naturally.",
        },
        {
          icon: "🧠",
          title: "1-on-1 Mental Health & Lactation Coaching",
          desc: "Monthly psychology sessions to navigate anxiety and emotional shifts, plus pre-delivery lactation guidance so you step into postpartum life fully prepared and confident.",
        },
        {
          icon: "🧘",
          title: "80+ Live Yoga & 20+ Guided Meditations",
          desc: "Led by certified prenatal experts. Safely build physical strength, manage aches, and cultivate deep emotional calm to prepare your body for a smoother delivery.",
        },
      ],
    },
    {
      group: "4. Bonding, Education & Premium Celebrations",
      cards: [
        {
          icon: "✨",
          title: "Daily Garbhasanskar Rituals",
          desc: "A complete 10-month program featuring music therapy, storytelling, and positivity sessions to actively nurture your baby’s emotional intelligence right from the womb.",
        },
        {
          icon: "👨‍👩‍👧",
          title: "Inclusive Parenting & Monthly Reviews",
          desc: "Specialized monthly sessions designed to prepare both partners and family members, alongside a dedicated monthly care review with your pregnancy counselor.",
        },
        {
          icon: "📖",
          title: "Curated Pregnancy Education",
          desc: "Engaging, week-by-week learning modules that decode your baby's development and bodily changes in simple, empowering terms.",
        },
        {
          icon: "🎁",
          title: "Exclusive Premium Giveaways",
          desc: "Receive a comprehensive Pregnancy Care Kit upon joining, a Baby Born Kit for delivery, and beautifully curated surprise gift hampers every single month to celebrate your progress.",
        },
      ],
    },
  ];

  const maxCareSlide = Math.max(0, cherishSupportCards.length - 1);

  const goToCareSlide = (index) => {
    const nextIndex = Math.max(0, Math.min(maxCareSlide, index));

    if (window.innerWidth < 992 && supportViewportRef.current) {
      const cards = Array.from(
        supportViewportRef.current.querySelectorAll(".cherish-support-card")
      );
      const targetCard = cards[nextIndex];

      if (targetCard) {
        supportViewportRef.current.scrollTo({
          left: targetCard.offsetLeft,
          behavior: "smooth",
        });
      }
    }

    setActiveCareSlide(nextIndex);
  };

  const handlePrevCareSlide = () => {
    goToCareSlide(activeCareSlide - 1);
  };

  const handleNextCareSlide = () => {
    goToCareSlide(activeCareSlide + 1);
  };

  useEffect(() => {
    const node = supportViewportRef.current;

    if (!node || window.innerWidth >= 992) {
      return undefined;
    }

    let isPaused = false;

    const pauseSlider = () => {
      isPaused = true;
    };

    const resumeSlider = () => {
      isPaused = false;
    };

    const intervalId = window.setInterval(() => {
      if (isPaused) {
        return;
      }

      const nextIndex = activeCareSlide >= maxCareSlide ? 0 : activeCareSlide + 1;
      goToCareSlide(nextIndex);
    }, 2000);

    node.addEventListener("touchstart", pauseSlider, { passive: true });
    node.addEventListener("touchend", resumeSlider);
    node.addEventListener("touchcancel", resumeSlider);

    return () => {
      window.clearInterval(intervalId);
      node.removeEventListener("touchstart", pauseSlider);
      node.removeEventListener("touchend", resumeSlider);
      node.removeEventListener("touchcancel", resumeSlider);
    };
  }, [activeCareSlide, maxCareSlide]);

  useEffect(() => {
    if (window.innerWidth >= 992) {
      return undefined;
    }

    const cleanups = ecosystemRowRefs.current
      .filter(Boolean)
      .map((node, rowIndex) => {
        let isPaused = false;

        const autoSlide = () => {
          const cards = Array.from(node.querySelectorAll(".ecosystem-card"));

          if (isPaused || cards.length === 0) {
            return;
          }

          ecosystemAutoIndexRef.current[rowIndex] =
            ((ecosystemAutoIndexRef.current[rowIndex] ?? 0) + 1) % cards.length;

          node.scrollTo({
            left: cards[ecosystemAutoIndexRef.current[rowIndex]].offsetLeft,
            behavior: "smooth",
          });
        };

        const pauseSlider = () => {
          isPaused = true;
        };

        const resumeSlider = () => {
          isPaused = false;
        };

        node.addEventListener("touchstart", pauseSlider, { passive: true });
        node.addEventListener("touchend", resumeSlider);
        node.addEventListener("touchcancel", resumeSlider);
        node.addEventListener("pointerdown", pauseSlider);
        node.addEventListener("pointerup", resumeSlider);
        node.addEventListener("pointercancel", resumeSlider);

        const intervalId = window.setInterval(autoSlide, 2000);

        return () => {
          window.clearInterval(intervalId);
          node.removeEventListener("touchstart", pauseSlider);
          node.removeEventListener("touchend", resumeSlider);
          node.removeEventListener("touchcancel", resumeSlider);
          node.removeEventListener("pointerdown", pauseSlider);
          node.removeEventListener("pointerup", resumeSlider);
          node.removeEventListener("pointercancel", resumeSlider);
        };
      });

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, []);


  return (
    <div className="cherish-page">
      <Header userid={userid} />

      {/* Hero Section - Matching the Screenshot */}
      <section className="hero-banner">
        <div className="container py-5">
          <div className="row align-items-center min-vh-75">
            <div className="col-md-6 px-lg-5 order-2 order-md-1">
              <h1 className="display-4 fw-bold text-coral mb-4">
                The Signature Care Ecosystem: Your 270-Day Sanctuary
              </h1>
              <p className="lead text-muted mb-5">
                Step out of the chaotic, fragmented medical system and into a beautifully
                orchestrated journey. This isn't just a care package; it’s a complete transformation of how
                motherhood is experienced. We handle the clinical precision, the daily tracking, and the expert
                coordination, so you can focus entirely on the joy of creating life.
              </p>
              <Link to="/pricing" className="btn btn-cherish-lg">
                Buy Now
              </Link>
            </div>
            <div className="col-md-6 mt-4 mt-md-0 order-1 order-md-2">
              <img src="/cherishcareH.png" className="img-fluid floating-img" alt="Pregnancy Journey" />
            </div>
          </div>
        </div>
      </section>

      {/* Alternating Content Sections */}
       <div className="main-combined-wrapper">
      <div className="journey-section" ref={containerRef}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="journey-title">
              Pregnancy is beautiful...but it can feel overwhelming
            </h2>
            <p className="journey-subtitle">
              Get all the answers and support you need.
            </p>
          </div>

          <div 
            className="row journey-cards-row" 
            ref={rowRef} 
            onScroll={handleScroll}
          >
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`col-lg-4 col-md-6 mb-4 journey-card-wrapper ${
                  steps.length === 5 && index === 3 ? "offset-lg-2" : ""
                }`}
              >
                <div className="journey-card">
                  <div className="card-img-wrapper">
                    <img src={step.img} alt={step.title} />
                  </div>
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                  {/* <span className="journey-step-tag">{`Step ${step.id}`}</span> */}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <section className="cherish-support-strip">
        <div className="container">
          <div className="cherish-support-strip__viewport" ref={supportViewportRef}>
            <div
              className="cherish-support-strip__track"
              style={{ transform: `translateX(-${activeCareSlide * 873}px)` }}
            >
              {cherishSupportCards.map((card, index) => (
                <article className="cherish-support-card" key={index}>
                  <div className="cherish-support-card__image">
                    <img src={card.img} alt={card.title} />
                  </div>
                  <div className="cherish-support-card__body">
                    <h3>{card.title}</h3>
                    <p>{card.desc}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
         
          <div className="cherish-support-strip__controls">
               <button
                 type="button"
                 className="cherish-support-strip__button"
                 onClick={handlePrevCareSlide}
                 disabled={activeCareSlide === 0}
               >
                 &larr;
               </button>
               <button
                 type="button"
                 className="cherish-support-strip__button"
                 onClick={handleNextCareSlide}
                 disabled={activeCareSlide === maxCareSlide}
               >
                 &rarr;
               </button>
             </div>
           </div>
      </section>

  <section className="cherish-feature-alternating">
     <div className="container">
       {/* Feature 1: Hamper */}
       <div className="feature-row">
         <div className="feature-image-side">
           <div className="feature-img-container">
             <img 
               src="/LRcard3.png" 
               alt="A special Hamper To Pamper" 
               className="feature-img"
             />
           </div>
         </div>
         <div className="feature-text-side">
           <h3>Empowering Your Natural Strength</h3>
           <p>
             We don't treat pregnancy like a surgical emergency. We are fierce advocates for normal
             deliveries. Our daily health tracking, holistic approach, proactive coaching, pelvic floor
             preparation, and continuous engagement are intentionally designed to safely maximize your
             chances of a natural birth, avoiding unnecessary C-sections.
           </p>
         </div>
       </div>
   
       {/* Feature 2: Tech System */}
       <div className="feature-row reverse">
         <div className="feature-image-side">
           <div className="feature-img-container">
             <img 
               src="/LRcard4.png" 
               alt="Fully-Remote Cutting-Edge Tech System" 
               className="feature-img"
             />
           </div>
         </div>
         <div className="feature-text-side">
           <h3>Uncompromising Clinical Precision</h3>
           <p>
             We don’t guess; we know daily. We have mapped out 40+ critical pathology and genetic tests
             (done conveniently from your home), daily vitals, symptoms, sleep pattern, mood tracking, and 9
             strategically timed ultrasounds at your fingertips. Every developmental milestone is meticulously
             monitored and explained to you on a daily basis.
           </p>
         </div>
       </div>
           <div className="feature-row">
         <div className="feature-image-side">
           <div className="feature-img-container">
             <img 
               src="/LRcard5.png" 
               alt="A special Hamper To Pamper" 
               className="feature-img"
             />
           </div>
         </div>
         <div className="feature-text-side">
           <h3>Celebrated Every Single Day</h3>
           <p>
             Because you are not just another patient for us; you are a mother and woman embarking on
             life's greatest journey. We turn every day of your nine-month pregnancy journey into a
             celebration, not a struggle. Expect a premium medical care kit upon joining, followed by
             exclusive surprise gift hampers delivered to your door every month. We make sure every
             milestone feels like a celebration.
           </p>
         </div>
       </div>
   
       {/* Feature 2: Tech System */}
       <div className="feature-row reverse">
         <div className="feature-image-side">
           <div className="feature-img-container">
             <img 
               src="/LRcard1.png" 
               alt="Fully-Remote Cutting-Edge Tech System" 
               className="feature-img"
             />
           </div>
         </div>
         <div className="feature-text-side">
           <h3>Midnight Panic, Solved in Seconds</h3>
           <p>
             Nausea at 2 AM? A sudden dietary doubt? The traditional system makes you wait until the next visit. With Myprega, your medical team is instantly accessible via our app. We bring the top-tier clinical authority straight to your bedroom, 24/7.
           </p>
         </div>
       </div>
     </div>
   </section>

   <section className="cherish-ecosystem-section">
     <img src="/Beyondtop.png" className="top-edge" alt="" />
     <div className="container">
       <div className="cherish-ecosystem-header text-center">
         <h2>Total Transparency. Uncompromising Value.</h2>
         <p>
           You deserve to know exactly what true, comprehensive care looks like.
           Here is the complete blueprint of your 270-day journey. No hidden fees.
           No missing pieces. Just absolute peace of mind, meticulously planned
           for you.
         </p>
       </div>
   
       {ecosystemSections.map((section) => (
         <div className="ecosystem-group" key={section.group}>
           <h3 className="ecosystem-group__title">{section.group}</h3>
           <div
             className="ecosystem-cards-row"
             ref={(node) => {
               const index = ecosystemSections.findIndex(
                 (item) => item.group === section.group
               );
               ecosystemRowRefs.current[index] = node;
             }}
           >
             {section.cards.map((card) => (
               <article className="ecosystem-card" key={card.title}>
                 <div className="ecosystem-card__icon" aria-hidden="true">
                   {card.icon}
                 </div>
                 <div className="ecosystem-card__body">
                   <h4>{card.title}</h4>
                   <p>{card.desc}</p>
                 </div>
               </article>
             ))}
           </div>
         </div>
       ))}
     </div>
     <img src="/BeyondbottoM.png" className="bottom-edge" alt="" />
   </section>
   
    <section 
        className="stats-section-wrapper"
        style={{ 
          backgroundImage: 'url("/NumberupB.png")', 
          backgroundSize: 'cover', 
          backgroundPosition: 'center',
          padding: '120px 0',
          textAlign: 'center',
          position: 'relative'
        }}
      >
        <img src="/Beyondtop.png" className="top-edge" alt="" />
        <div className="container">
          {/* Section Header */}
          <div className="stats-section-header">
            <h3 className="stats-section-title">
              Astonishingly Awesome, Price At Rock-Bottom
            </h3>
            {/* <h3 className="stats-section-title stats-section-title-accent">
              myprega.com
            </h3> */}
             <p className="journey-subtitle">
               Make your journey experience memorable at a pocket-friendly price.
             </p>
          </div>
          <Link to="/pricing" className="btn btn-cherish-lg px-5 shadow">
           GET STARTED NOW
         </Link>
        </div>
          <img src="/BeyondbottoM.png" className="bottom-edge" alt="" />
    </section>
 </div>

      <Footer />
      <InteraktScript />
    </div>
  );
}

export default Cherishcare;
